import type { LoaderFunction, MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Layout } from '~/components/Layout'
import { Article } from '~/components/ResumeExample/Article'
import { Colors } from '~/styles/colors'
import { addIdToHeadings } from '~/utils/contentfulUtils'
import type { TypeResumeExample, TypeResumeExampleSkeleton } from '~/types/contentful'
import {
  PageContext,
  preparePageContext,
  prepareRemixRequest,
  prepareRobotsMetadata,
  previewRouteHandler,
} from '~/utils/routes/routeHelper'
import { CIOEntries } from '~/libs/contentful/types/entries'
import ContentfulClientManager from '../libs/contentful/ContentfulClientManager'
import { parse, stringify } from 'flatted'

export const loader: LoaderFunction = async ({ params, request }) => {
  if (!params.slug) {
    throw new Response('Not Found', { status: 404 })
  }

  const url = prepareRemixRequest(request)
  const { preview } = previewRouteHandler(url)
  const contextValue = preparePageContext(url)

  const entries = await ContentfulClientManager.getContentfulEntries<TypeResumeExampleSkeleton>(
    CIOEntries.resumeExample,
    {
      preview,
      'fields.slug': params.slug,
      'fields.type': 'Cover Letter',
      limit: 1,
    },
  )

  if (!entries || entries.items?.length === 0) {
    throw new Response('Not Found', { status: 404 })
  }

  const resume = entries.items[0]
  // move this when the logic is moved from the contentful api to the contentful client
  addIdToHeadings(resume.fields.body)

  return { resumeExample: stringify(resume), contextValue }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  let { resumeExample } = data || {}
  resumeExample = parse(resumeExample)
  let metaTitle, metaDescription
  const metaRobots = prepareRobotsMetadata(data.contextValue.url)

  if (resumeExample) {
    ;({ metaTitle, metaDescription } = resumeExample.fields)
  }

  return [
    {
      title: metaTitle ? metaTitle : undefined,
    },
    {
      name: 'description',
      content: metaDescription ? metaDescription : undefined,
    },
    { name: 'robots', content: metaRobots ? metaRobots : undefined },
  ]
}

type ResumeExampleLoaderData = {
  resumeExample: string
  contextValue: PageContext
}

export default function ResumeExample() {
  const { resumeExample } = useLoaderData<typeof loader>() as ResumeExampleLoaderData

  const example = parse(resumeExample) as TypeResumeExample<'WITHOUT_UNRESOLVABLE_LINKS', string>

  return (
    <Layout headerBgColor={Colors.Neutral5} isFlatHeader>
      <Layout.Content style={{ backgroundColor: Colors.White }}>
        <Article data={example} type="coverLetter" />
      </Layout.Content>
    </Layout>
  )
}
